import classNames from 'classnames';
import React from 'react';

import {whenBrazeReady} from '@udemy/braze';
import {AddToCartEvent, BuyablesAddedToCart, CartBuyable} from '@udemy/cart';
import {onEnterAndSpace} from '@udemy/design-system-utils';
import {Tracker} from '@udemy/event-tracking';
import {useI18n} from '@udemy/i18n';
import {Button} from '@udemy/react-core-components';
import {ShoppingClient, ShoppingItem as UdemyShoppingItem} from '@udemy/shopping';
import {useUDData, AuthenticatedUser} from '@udemy/ud-data';

import styles from './shopping-item.module.less';
import {ActionHandlers, ActionSupportedLayoutsProps} from './types';

interface ActionsProps {
    item: UdemyShoppingItem;
    listName: string;
    actions: ActionHandlers;
    showSaveForLaterAction?: boolean;
    layout: ActionSupportedLayoutsProps;
}

export interface Appboy {
    isPushPermissionGranted: () => boolean;
    logCustomEvent(name: string, props: Record<string, number[]>): void;
}

export const Actions = ({
    item,
    listName,
    actions,
    showSaveForLaterAction,
    layout,
}: ActionsProps) => {
    const {gettext} = useI18n();
    const {me: udMe} = useUDData();
    const isListNameCart = listName === 'cart';
    const isPaid = item.list_price && item.list_price.amount > 0;

    function handleMoveToCart() {
        actions.moveToList('cart', item)();
        const buyable = item.buyable;
        Tracker.publishEvent(
            new AddToCartEvent({
                buyables: [
                    {
                        type: 'course',
                        id: buyable.id,
                        trackingId: buyable.frontendTrackingId,
                    } as CartBuyable,
                ],
                uiRegion: 'cart',
            }),
        );

        /**
         * @note: We're moving away from the AddToCartEvent and using BuyablesAddedToCart (v2 event) instead.
         * Until we make sure that there is no regression, we'll keep sending both events.
         */
        Tracker.publishEvent(
            new BuyablesAddedToCart({
                buyables: [
                    {
                        type: 'course',
                        id: buyable.id,
                        trackingId: buyable.frontendTrackingId || buyable.tracking_id,
                    } as CartBuyable,
                ],
                uiRegion: 'cart',
            }),
        );
        sendBrazeEvent(item.buyable.id);
    }

    function sendBrazeEvent(courseId: number) {
        whenBrazeReady((appboy: Appboy) => {
            if (udMe.is_authenticated) {
                return;
            }

            if (appboy.isPushPermissionGranted()) {
                // Only trigger event for the first course added to the cart
                // if the same visitor adds multiple courses to their cart
                if (ShoppingClient.lists.cart.isEmpty) {
                    appboy.logCustomEvent('UserCartAbandonment', {
                        courses_in_cart: [courseId],
                    });
                }
            }
        });
    }

    const buttonProps = {
        typography: 'ud-text-sm',
        udStyle: 'ghost',
        size: 'xsmall',
        tabIndex: 0,
    } as const;

    const isItemCertificationVoucher = item.buyable.buyable_object_type === 'certification_voucher';
    return (
        <div className={classNames(styles[`${layout}-actions`])}>
            <Button
                {...buttonProps}
                onClick={actions.remove(item)}
                onKeyPress={onEnterAndSpace(actions.remove(item))}
            >
                {gettext('Remove')}
            </Button>
            {showSaveForLaterAction && isListNameCart && (
                <Button
                    {...buttonProps}
                    onClick={actions.moveToList('saved_for_later', item)}
                    onKeyPress={onEnterAndSpace(actions.moveToList('saved_for_later', item))}
                >
                    {gettext('Save for Later')}
                </Button>
            )}
            {isListNameCart && !isItemCertificationVoucher && (udMe as AuthenticatedUser).id && (
                <Button
                    {...buttonProps}
                    onClick={actions.moveToList('wishlist', item)}
                    onKeyPress={onEnterAndSpace(actions.moveToList('wishlist', item))}
                >
                    {gettext('Move to Wishlist')}
                </Button>
            )}
            {!isListNameCart && isPaid && (
                <Button
                    {...buttonProps}
                    onClick={handleMoveToCart}
                    onKeyPress={onEnterAndSpace(handleMoveToCart)}
                >
                    {gettext('Move to Cart')}
                </Button>
            )}
        </div>
    );
};
